import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Copy, Facebook, MessageCircle, MessageSquare } from "lucide-react";
import { toast } from "sonner";

export default function ShareDialog({ 
  isOpen, 
  onOpenChange, 
  trackId, 
  trackTitle, 
  artistName 
}) {
  // Safety check - don't render if required props are missing
  if (typeof isOpen === 'undefined' || typeof onOpenChange === 'undefined') {
    return null;
  }

  // Generate track URL for sharing
  const getTrackUrl = () => {
    if (typeof window === 'undefined') return '';
    if (!trackId) {
      // Fallback to current page URL if no trackId provided
      return window.location.href;
    }
    return `${window.location.origin}/TrackDetails?id=${trackId}`;
  };

  // Share text for social media
  const getShareText = () => {
    // Always include track info if available, otherwise use generic message
    if (trackTitle && artistName) {
      return `Check out "${trackTitle}" by ${artistName} on Soundope!`;
    } else if (trackTitle) {
      return `Check out "${trackTitle}" on Soundope!`;
    } else {
      return "Check out this track on Soundope!";
    }
  };

  // Share handlers
  const handleCopyLink = async () => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return;
    try {
      await navigator.clipboard.writeText(getTrackUrl());
      toast.success("Link copied to clipboard!");
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to copy link:", error);
      toast.error("Failed to copy link");
    }
  };

  const handleShareFacebook = () => {
    if (typeof window === 'undefined') return;
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getTrackUrl())}`;
    window.open(url, "_blank", "width=600,height=400");
  };

  const handleShareX = () => {
    if (typeof window === 'undefined') return;
    const url = `https://twitter.com/intent/tweet?url=${encodeURIComponent(getTrackUrl())}&text=${encodeURIComponent(getShareText())}`;
    window.open(url, "_blank", "width=600,height=400");
  };

  const handleShareInstagram = () => {
    // Instagram doesn't support direct URL sharing, so we'll copy the link
    handleCopyLink();
    toast.info("Instagram doesn't support direct links. Link copied - paste it in your story or DM!");
  };

  const handleShareMessenger = () => {
    // Fallback to copy link if app_id not available
    handleCopyLink();
    toast.info("Messenger link copied - paste it in a message!");
  };

  const handleShareSMS = () => {
    if (typeof window === 'undefined') return;
    const url = `sms:?body=${encodeURIComponent(getShareText() + " " + getTrackUrl())}`;
    window.location.href = url;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="neuro-base border-[#1f1f1f] bg-[#141414] text-[#d0d0d0] max-w-md">
        <DialogHeader>
          <DialogTitle className="text-[#d0d0d0] font-['Rotten_Valley'] text-xl">
            Share Track
          </DialogTitle>
          <DialogDescription className="text-[#808080] text-sm">
            {trackTitle && artistName ? (
              <>Share &quot;{trackTitle}&quot; by {artistName}</>
            ) : (
              "Share this track"
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-4">
          {/* Copy Link */}
          <Button
            onClick={handleCopyLink}
            className="w-full neuro-base active:neuro-pressed rounded-xl py-3 text-left justify-start gap-3"
            variant="outline"
          >
            <Copy className="h-5 w-5" />
            <span>Copy Link</span>
          </Button>

          {/* Facebook */}
          <Button
            onClick={handleShareFacebook}
            className="w-full neuro-base active:neuro-pressed rounded-xl py-3 text-left justify-start gap-3"
            variant="outline"
          >
            <Facebook className="h-5 w-5 text-blue-500" />
            <span>Share on Facebook</span>
          </Button>

          {/* X (Twitter) */}
          <Button
            onClick={handleShareX}
            className="w-full neuro-base active:neuro-pressed rounded-xl py-3 text-left justify-start gap-3"
            variant="outline"
          >
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
            <span>Share on X</span>
          </Button>

          {/* Instagram */}
          <Button
            onClick={handleShareInstagram}
            className="w-full neuro-base active:neuro-pressed rounded-xl py-3 text-left justify-start gap-3"
            variant="outline"
          >
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
            </svg>
            <span>Share on Instagram</span>
          </Button>

          {/* Messenger */}
          <Button
            onClick={handleShareMessenger}
            className="w-full neuro-base active:neuro-pressed rounded-xl py-3 text-left justify-start gap-3"
            variant="outline"
          >
            <MessageSquare className="h-5 w-5 text-blue-500" />
            <span>Share via Messenger</span>
          </Button>

          {/* SMS */}
          <Button
            onClick={handleShareSMS}
            className="w-full neuro-base active:neuro-pressed rounded-xl py-3 text-left justify-start gap-3"
            variant="outline"
          >
            <MessageCircle className="h-5 w-5" />
            <span>Share via SMS</span>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

